/**
 * Authentication API service for backend integration
 * Handles register, login, and refresh token operations
 */

import { apiFetch } from "./apiClient";
import { authClient, oauthClient } from '../../../backend/src/client';

const authApi = authClient(apiFetch);
const oauthApi = oauthClient(apiFetch) as any;

const LOCAL_WEB_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

const resolveOAuthBaseUrl = (): string => {
  if (typeof window !== "undefined" && LOCAL_WEB_HOSTS.has(window.location.hostname)) {
    return window.location.origin;
  }

  const configured = import.meta.env.VITE_API_BASE_URL;
  if (typeof configured === "string" && configured.length > 0) {
    return configured;
  }

  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return "";
};
/**
 * User data returned from backend
 */
export type AuthUser = {
  id: string;
  email: string;
};

/**
 * Backend authentication response
 */
export type AuthResponse = {
  token: string;
  refreshToken: string;
  user: AuthUser;
};

/**
 * Register request payload
 */
export type RegisterRequest = {
  email: string;
  password: string;
};

/**
 * Login request payload
 */
export type LoginRequest = {
  email: string;
  password: string;
};

/**
 * Refresh token request payload
 */
export type RefreshTokenRequest = {
  refreshToken: string;
};

export type RefreshTokenResponse = {
  token: string;
  refreshToken: string;
};

type JsonResponseLike = Pick<Response, "json" | "status" | "statusText" | "ok">;

const parseApiErrorMessage = async (response: JsonResponseLike, fallback: string): Promise<string> => {
  try {
    const payload = (await response.json()) as { message?: unknown; error?: unknown };
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
  } catch {
    // Ignore malformed payloads and use the fallback.
  }

  return fallback;
};

/**
 * Register a new user account
 *
 * @param credentials - Email and password
 * @returns Auth response with token, refreshToken, and user data
 * @throws Error if registration fails
 */
export async function register(
  credentials: RegisterRequest
): Promise<AuthResponse> {
  const response = await authApi.register.$post({
    json: credentials,
  });

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, `Registration failed: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Authenticate with existing credentials
 *
 * @param credentials - Email and password
 * @returns Auth response with token, refreshToken, and user data
 * @throws Error if authentication fails
 */
export async function login(credentials: LoginRequest): Promise<AuthResponse> {
  const response = await authApi.login.$post({
    json: credentials,
  });

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, `Login failed: ${response.statusText}`));
  }

  return response.json();
}



/**
 * Refresh an expired access token
 *
 * @param refreshToken - The refresh token from previous login/register
 * @returns Refreshed token pair
 * @throws Error if refresh fails
 */
export async function refreshAccessToken(
  refreshToken: string
): Promise<RefreshTokenResponse> {
  const response = await authApi.refresh.$post({
    json: { refreshToken },
  });

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, `Token refresh failed: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Exchange a Google ID token (from the native Sign in with Google SDK) for
 * app JWT tokens. Used on Android instead of the redirect-based OAuth flow.
 */
export async function exchangeGoogleIdToken(idToken: string): Promise<AuthResponse> {
  const response = await oauthApi.google.native.$post(undefined, {
    init: {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    },
  });

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, `Google sign-in failed: ${response.statusText}`));
  }

  return response.json();
}

/**
 * Get the Google OAuth URL to redirect user to
 * This endpoint initiates the OAuth flow and returns the Google authorization URL
 *
 * @returns The Google authorization URL to redirect to
 * @throws Error if OAuth initiation fails
 */
export async function getGoogleOAuthUrl(): Promise<string> {
  return `${resolveOAuthBaseUrl()}/auth/oauth/google`;
}

/**
 * Exchange OAuth code for JWT tokens
 *
 * @param code - Temporary OAuth code from callback
 * @returns Auth response with token, refreshToken, and user data
 * @throws Error if exchange fails
 */
export async function exchangeOAuthCode(
  code: string
): Promise<AuthResponse> {
  const response = await authApi.oauth['token-exchange'].$post({
    json: { code },
  });

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, `OAuth token exchange failed: ${response.statusText}`));
  }

  return response.json();
}
